import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { waLink } from "@/api";
import { useLang, tr } from "@/i18n";

const HERO_IMG = "/img/river-rituals.jpg";

export const Hero = ({ whatsapp }) => {
    const ref = useRef(null);
    const { lang } = useLang();
    const t = (s) => tr(lang, s);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

    return (
        <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden grain-overlay">
            <motion.div className="absolute inset-0" style={{ y, scale }}>
                <img
                    src={HERO_IMG}
                    alt="Pinddaan rituals at Falgu River, Gaya"
                    className="w-full h-full object-cover"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#140f0c]/95 via-[#140f0c]/55 to-[#140f0c]/40" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#140f0c]/60 via-transparent to-transparent" />

            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-40 w-full">
                <div className="max-w-2xl bg-[#140f0c]/45 backdrop-blur-md rounded-3xl border border-white/10 p-7 sm:p-10 shadow-[0_24px_64px_rgba(0,0,0,0.45)]">
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.9, duration: 0.8 }}
                        className="text-xs uppercase tracking-[0.35em] text-amber-300 mb-6"
                        data-testid="hero-overline"
                    >
                        {t("Sacred Rituals · Gaya, Bihar")}
                    </motion.p>

                    <h1
                        className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.12] tracking-tight [text-shadow:0_2px_20px_rgba(0,0,0,0.65)]"
                        data-testid="hero-headline"
                    >
                        <MaskedLine delay={0.15}>{t("Authentic Pinddaan,")}</MaskedLine>
                        <MaskedLine delay={0.3}>{t("Shradh & Tarpan")}</MaskedLine>
                        <MaskedLine delay={0.45}>
                            <span className="font-serif-alt italic font-medium text-amber-200">{t("at the Holy Vedis of Gaya")}</span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.75, duration: 0.8 }}
                        className="mt-6 text-base md:text-lg text-stone-200 leading-relaxed [text-shadow:0_1px_10px_rgba(0,0,0,0.6)]"
                        data-testid="hero-subtitle"
                    >
                        {t("Complete rituals performed by traditional Gayawal Pandas at Vishnupad Temple, Falgu River, Akshayavat and the sacred vedis of Gaya — with transparent 40% advance booking.")}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.95, duration: 0.8 }}
                        className="mt-9 flex flex-wrap gap-4"
                    >
                        <a
                            href="#packages"
                            data-testid="hero-view-packages-button"
                            className="px-8 py-3.5 rounded-full bg-[#C85A17] text-white font-medium hover:bg-[#9C370B] transition-colors duration-300 shadow-[0_8px_24px_rgba(200,90,23,0.4)]"
                        >
                            {t("View Packages")}
                        </a>
                        <a
                            href={waLink(whatsapp, "Namaste, I would like to know more about Gaya Pinddaan services.")}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="hero-whatsapp-button"
                            className="px-8 py-3.5 rounded-full border border-white/40 text-white font-medium hover:bg-white/10 transition-colors duration-300 flex items-center gap-2"
                        >
                            <MessageCircle size={18} /> {t("Chat on WhatsApp")}
                        </a>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
